import React, { useRef, useState, useEffect } from "react";
import { useFormContext, useController } from "react-hook-form";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import ImageIcon from "@material-ui/icons/Image";
import CloseIcon from "@material-ui/icons/Close";
import Webcam from "react-webcam";
import useCamera from "../../../../../../hooks/useCamera";
import { useStore } from "../../../../../../hooks/useStore";
import NotCamera from "../../../../../../components/Scan/NotCamera/NotCamera";
import DocumentImageViewer from "../../../../../../components/common/PL/PicturePreview/DocumentImageViewer";
import useShowImage from "../../../../../../hooks/useShowImage";
import useImage from "../../../../../../components/common/PL/PicturePreview/useImage";
import { dataURLtoFile } from "../../../../../../utils/imageUtil";
import "./ObservationsMultiImageUpload.css";

interface ObservationsMultiImageUploadProps {
  names: string[];
  fileNames: string[];
  label: string;
}

interface SlotInfo {
  name: string;
  fileName: string;
  imageUrl: string;
  value: File | string | null;
}

const ObservationsMultiImageUpload = ({
  names,
  fileNames,
  label,
}: ObservationsMultiImageUploadProps) => {
  const { control } = useFormContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const getImage = useShowImage();
  const { uploadImage } = useImage();

  const { field: field1 } = useController({ name: names[0], control });
  const { field: field2 } = useController({ name: names[1], control });
  const { field: field3 } = useController({ name: names[2], control });
  const fields = [field1, field2, field3];

  const [imageUrls, setImageUrls] = useState<string[]>(["", "", ""]);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const { getListOfCameras } = useCamera();
  const { cameraStore } = useStore();

  useEffect(() => {
    const urls: string[] = ["", "", ""];
    const cleanups: (() => void)[] = [];
    fields.forEach((field, i) => {
      if (field.value instanceof File) {
        const fileUrl = URL.createObjectURL(field.value);
        urls[i] = fileUrl;
        cleanups.push(() => URL.revokeObjectURL(fileUrl));
      } else if (typeof field.value === "string" && field.value) {
        urls[i] = getImage(field.value);
      }
    });
    setImageUrls(urls);
    return () => cleanups.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field1.value, field2.value, field3.value]);

  const uploadedSlots: SlotInfo[] = [];
  let nextSlotIndex: number | null = null;

  fields.forEach((field, i) => {
    if (field.value) {
      uploadedSlots.push({
        name: names[i],
        fileName: fileNames[i],
        imageUrl: imageUrls[i],
        value: field.value,
      });
    } else if (nextSlotIndex === null) {
      nextSlotIndex = i;
    }
  });

  const uploadedCount = uploadedSlots.length;
  const remainingCount = 3 - uploadedCount;
  const canUpload = remainingCount > 0;

  const validateFile = (file: File): boolean => {
    const isAcceptedType =
      file.type === "image/png" ||
      file.type === "image/jpeg" ||
      file.name.toLowerCase().endsWith(".png") ||
      file.name.toLowerCase().endsWith(".jpg") ||
      file.name.toLowerCase().endsWith(".jpeg");
    const isAcceptedSize = file.size <= 10 * 1024 * 1024;
    if (!isAcceptedType) {
      alert("Solo se permiten imágenes en formato PNG o JPEG/JPG.");
      return false;
    }
    if (!isAcceptedSize) {
      alert("El tamaño máximo permitido para la imagen es de 10MB.");
      return false;
    }
    return true;
  };

  const uploadToNextSlot = (file: File, dataUrl: string) => {
    if (nextSlotIndex === null) return;
    const idx = nextSlotIndex;
    fields[idx].onChange(file);
    uploadImage(names[idx], file, dataUrl);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (!canUpload) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!validateFile(file)) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        uploadToNextSlot(file, dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && canUpload) {
      if (!validateFile(file)) {
        e.target.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        uploadToNextSlot(file, dataUrl);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const handleDropZoneClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!canUpload) return;
    setMenuAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (isCameraOpen) {
      getListOfCameras();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraOpen]);

  const stopWebcamTracks = () => {
    if (webcamRef.current) {
      if (webcamRef.current.stream) {
        webcamRef.current.stream.getTracks().forEach((track) => track.stop());
      }
      if (webcamRef.current.video?.srcObject) {
        try {
          const stream = webcamRef.current.video.srcObject as MediaStream;
          stream.getTracks().forEach((track) => track.stop());
        } catch (e) {
          console.error("Error stopping tracks:", e);
        }
        webcamRef.current.video.srcObject = null;
      }
    }
  };

  useEffect(() => {
    return () => {
      stopWebcamTracks();
    };
  }, []);

  const closeCameraDialog = () => {
    stopWebcamTracks();
    setIsCameraOpen(false);
  };

  const capture = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc || !canUpload) return;
    const file = dataURLtoFile(imageSrc, `observation-photo-${Date.now()}.jpg`);
    uploadToNextSlot(file, imageSrc);
    closeCameraDialog();
  };

  const handleDelete = (slotIndex: number) => {
    let fieldIdx = -1;
    let count = 0;
    for (let i = 0; i < 3; i++) {
      if (fields[i].value) {
        if (count === slotIndex) {
          fieldIdx = i;
          break;
        }
        count++;
      }
    }
    if (fieldIdx === -1) return;

    const remaining: { value: File | string | null; url: string }[] = [];
    for (let i = 0; i < 3; i++) {
      if (i !== fieldIdx && fields[i].value) {
        remaining.push({ value: fields[i].value, url: imageUrls[i] });
      }
    }

    for (let i = 0; i < 3; i++) {
      if (i < remaining.length) {
        fields[i].onChange(remaining[i].value);
      } else {
        fields[i].onChange(null);
      }
    }
  };

  return (
    <div className="multiImageUploadContainer">
      {label && <label className="multiImageUploadLabel">{label}</label>}

      {canUpload && (
        <div
          className={`multiImageDropZone ${isDragActive ? "dragActive" : ""}`}
          onClick={handleDropZoneClick}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleDropZoneClick(event as unknown as React.MouseEvent<HTMLElement>);
            }
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
        >
          <div className="multiImageDropZoneContent">
            <div className="multiImageDropZoneIconWrapper">
              <ImageIcon className="multiImageDropZoneIcon" />
            </div>
            <span className="multiImageDropZoneLabel">Foto de observaciones</span>
            <span className="multiImageDropZoneSublabel">
              Arrastrá o hacé clic · {remainingCount} foto
              {remainingCount !== 1 ? "s" : ""} restante
              {remainingCount !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg"
        className="multiImageHiddenInput"
        onChange={handleFileChange}
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            setIsCameraOpen(true);
          }}
        >
          <CameraAltIcon style={{ marginRight: 8 }} />
          Tomar foto
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            fileInputRef.current?.click();
          }}
        >
          <ImageIcon style={{ marginRight: 8 }} />
          Buscar archivo
        </MenuItem>
      </Menu>

      {uploadedSlots.length > 0 && (
        <div className="multiImageThumbnailStrip">
          {uploadedSlots.map((slot, idx) => (
            <div
              key={slot.name + idx}
              className="multiImageThumbnailWrapper"
              onClick={() => setViewerIndex(idx)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setViewerIndex(idx);
                }
              }}
            >
              <img src={slot.imageUrl} alt={slot.fileName} className="multiImageThumbnail" />
              <button
                type="button"
                className="multiImageThumbnailDelete"
                aria-label="Eliminar imagen"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(idx);
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={isCameraOpen}
        onClose={closeCameraDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        {isCameraOpen && (
          <>
            <DialogTitle>
              Foto de observaciones
              <IconButton
                aria-label="close"
                style={{ position: "absolute", right: 8, top: 8 }}
                onClick={closeCameraDialog}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                {cameraStore.empty ? (
                  <NotCamera />
                ) : (
                  <div
                    style={{
                      borderRadius: "12px",
                      overflow: "hidden",
                      backgroundColor: "#000",
                      minHeight: isMobile ? "56vh" : "420px",
                    }}
                  >
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        deviceId: cameraStore.deviceId || undefined,
                      }}
                      style={{
                        width: "100%",
                        height: isMobile ? "56vh" : "420px",
                        display: "block",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeCameraDialog}>Cancelar</Button>
              <Button
                onClick={capture}
                color="primary"
                variant="contained"
                disabled={cameraStore.empty}
              >
                Capturar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {viewerIndex !== null && uploadedSlots[viewerIndex] && (
        <DocumentImageViewer
          open={viewerIndex !== null}
          onClose={() => setViewerIndex(null)}
          imageUrl={uploadedSlots[viewerIndex]?.imageUrl || ""}
          alt={uploadedSlots[viewerIndex]?.fileName || "Foto de observaciones"}
          fileName={uploadedSlots[viewerIndex]?.fileName || "Foto de observaciones"}
          allowDelete
          onDelete={() => {
            const idx = viewerIndex;
            setViewerIndex(null);
            handleDelete(idx);
          }}
        />
      )}
    </div>
  );
};

export default ObservationsMultiImageUpload;
